import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { HeartInactive, Document } from '@johnlewispartnership/wtr-ingredients/foundations/icons';

import urls from 'constants/urls';
import SignIn from 'components/AuthenticationAction/SignIn';
import { LoadableFavouritesPage, LoadableMyOrdersPage } from 'components/App/loadableComponents';
import { isUserLoggedIn } from 'redux/modules/sessions/selectors';
import { yieldToMain } from 'utils/yield-to-main';
import { pushMenuClick } from 'utils/gtm';
import { useWtrSelector } from 'redux/hooks';
import LinkAsColumn from 'components/LinkAsColumn';

import MyAccount from '../LinksBar/MyAccount/MyAccount';
import styles from './AuthenticationLinks.scss';

type Breakpoint = 'xsMobile' | 'mobile' | 'tablet' | 'desktop' | 'xlDesktop';

const AuthenticationLinks = ({
  breakpoint,
  showMinSpend = false,
}: {
  breakpoint: Breakpoint;
  showMinSpend?: boolean;
}) => {
  const isLoggedIn = useWtrSelector(isUserLoggedIn);

  const menuClickHandler = useCallback((event: any) => {
    yieldToMain().then(() => {
      pushMenuClick(event.target.textContent);
    });
  }, []);

  const componentPreloadRef = useRef({
    favouritesPage: false,
    myOrdersPage: false,
  });

  const preloadFavourites = useCallback(() => {
    if (componentPreloadRef.current.favouritesPage) {
      return;
    }

    componentPreloadRef.current.favouritesPage = true;
    LoadableFavouritesPage.preload();
  }, []);

  const preloadMyOrders = useCallback(() => {
    if (componentPreloadRef.current.myOrdersPage) {
      return;
    }

    componentPreloadRef.current.myOrdersPage = true;
    LoadableMyOrdersPage.preload();
  }, []);

  const unauthenticatedAccountLinks = (
    <div data-testid="account-link-login" aria-label="Sign in and register">
      <SignIn
        className={styles.iconLinks}
        iconSize="small"
        iconClassName={styles.signInIcon}
        textClassName={styles.signInText}
        withRegister={false}
        newHeader
      />
    </div>
  );

  const Favourites = (
    <LinkAsColumn
      component={Link as unknown as 'a'}
      id="site-header-favourites"
      href={urls.favourites}
      componentProps={{ to: urls.favourites }}
      className={styles.iconLinks}
      data-testid="site-header-favourites"
      onClick={menuClickHandler}
      onMouseEnter={preloadFavourites}
      underline="never"
    >
      <HeartInactive size="small" />
      <span className={classNames(styles.signInText, { [styles.hasMinSpend]: showMinSpend })}>
        Favourites
      </span>
    </LinkAsColumn>
  );

  const MyOrders = (
    <LinkAsColumn
      component={Link as unknown as 'a'}
      id="site-header-orders"
      href={urls.myOrdersPage}
      componentProps={{ to: urls.myOrdersPage }}
      className={styles.iconLinks}
      data-testid="site-header-orders"
      onClick={menuClickHandler}
      onMouseEnter={preloadMyOrders}
      underline="never"
    >
      <Document size="small" />
      <span className={classNames(styles.signInText, { [styles.hasMinSpend]: showMinSpend })}>
        Orders
      </span>
    </LinkAsColumn>
  );

  const hiddenFavouritesOn: Set<Breakpoint> = new Set(['xsMobile', 'tablet']);
  const isFavouritesVisible = !hiddenFavouritesOn.has(breakpoint);

  const hiddenMyAccountOn: Set<Breakpoint> = new Set(['xsMobile', 'mobile']);
  const isMyAccountVisible = !hiddenMyAccountOn.has(breakpoint);

  const isMyOrdersVisible = breakpoint === 'xlDesktop';

  const hasVisibleLinks = isFavouritesVisible || isMyOrdersVisible || isMyAccountVisible;

  const authenticatedAccountLinks = hasVisibleLinks ? (
    <div aria-label="Account links" className={styles.secondaryLinks}>
      {isFavouritesVisible && Favourites}
      {isMyOrdersVisible && MyOrders}
      {isMyAccountVisible && <MyAccount />}
    </div>
  ) : null;

  return !isLoggedIn ? unauthenticatedAccountLinks : authenticatedAccountLinks;
};

export default AuthenticationLinks;
